import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Button,
  FormFeedback,
  Input,
  Label,
  Badge,
} from 'reactstrap';
import { useFormik } from 'formik';
import { withTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BreadCrumb from '../../../Components/Common/BreadCrumb';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import { Portuguese } from 'flatpickr/dist/l10n/pt.js';
import CurrencyInput from '../../../Components/Common/CurrencyInput';
import { AbilityContext } from '../../../helpers/casl';
import Yup from '../../../helpers/yup_extras';
import campaignService from '../../../helpers/campaign_service';
import { getCampaignErrorMessage } from '../../../helpers/campaignErrorHandler';

const EditCampaign = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const ability = useContext(AbilityContext);
  document.title = 'Editar Campanha | Opencashback';

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [campaign, setCampaign] = useState(null);

  const toNumber = (value) => {
    if (!value) return 0;
    return Number(String(value).replaceAll('.', '').replace(',', '.'));
  };

  const parseDateString = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString + 'T00:00:00');
    return isNaN(date.getTime()) ? null : date;
  };

  const parseTimeString = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  useEffect(() => {
    const fetchCampaign = async () => {
      setIsLoading(true);
      try {
        const foundCampaign = await campaignService.getCampaignById(id);
        if (foundCampaign) {
          setCampaign(foundCampaign);
        } else {
          toast.error(props.t('campaigns.messages.fetch-error'));
          navigate('/estrategias/campanhas');
        }
      } catch (error) {
        console.error('Error fetching campaign:', error);
        toast.error(props.t('campaigns.messages.fetch-error'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaign();
  }, [id, navigate, props]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required(props.t('validation-required-name')),
        startDate: Yup.string().required(
          props.t('validation-required-start-date')
        ),
        endDate: Yup.string().required(props.t('validation-required-end-date')),
        generation: Yup.string().required(
          props.t('validation-required-generation-type')
        ),
        cashback: Yup.string()
          .required(props.t('validation-required-value'))
          .numberString(props.t('validation-number-value')),
        calculation_basis: Yup.string().required(
          props.t('validation-required-calculation-basis')
        ),
        days_to_expire: Yup.string().required(
          props.t('validation-required-expiration-time')
        ),
        minimum_order: Yup.string(),
        maximum_rescue: Yup.string(),
      }),
    [props]
  );

  const initialValues = useMemo(
    () => ({
      name: campaign?.name || '',
      startDate: campaign?.startDate ? campaign.startDate.split('T')[0] : '',
      startTime: campaign?.startDate
        ? new Date(campaign.startDate).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'America/Sao_Paulo',
          })
        : '',
      endDate: campaign?.endDate ? campaign.endDate.split('T')[0] : '',
      endTime: campaign?.endDate
        ? new Date(campaign.endDate).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'America/Sao_Paulo',
          })
        : '',
      status: campaign?.status || 'scheduled',
      generation: campaign?.cashbackType || '',
      cashback: campaign?.cashbackValue || '',
      calculation_basis: campaign?.calculationBasis || 'total',
      days_to_expire: campaign?.daysToExpire || 30,
      physical_store_days: campaign?.physicalStoreDays || 0,
      ecommerce_days: campaign?.ecommerceDays || 0,
      minimum_accumulation: campaign?.minimumAccumulation || 0,
      maximum_accumulation: campaign?.maximumAccumulation || 0,
      minimum_order: campaign?.minimumOrder || 0,
      rescue_value: campaign?.rescueValue || '',
      maximum_rescue: campaign?.maxRedemptionValue || '',
      minimum_purchase_redemption: campaign?.minimumPurchaseRedemption || '',
      block_discounts: campaign?.blockDiscounts || false,
      block_vtex_any_coupon: campaign?.blockVtexAnyCoupon || false,
      redemption_block_discounts: campaign?.redemptionBlockDiscounts || false,
      redemption_block_vtex_any_coupon:
        campaign?.redemptionBlockVtexAnyCoupon || false,
      redemption_block_vtex_price_remarked:
        campaign?.redemptionBlockVtexPriceRemarked || false,
    }),
    [campaign]
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSaving(true);

      const combineDateAndTime = (date, time) => {
        if (!date) return null;
        let hours = 0,
          minutes = 0;

        if (time) {
          const [h, m] = time.split(':').map(Number);
          hours = h;
          minutes = m;
        }

        const dateObj = new Date(date + 'T00:00:00');
        dateObj.setHours(hours, minutes, 0, 0);

        return dateObj;
      };

      const startDateTime = combineDateAndTime(
        values.startDate,
        values.startTime
      );
      const endDateTime = combineDateAndTime(values.endDate, values.endTime);

      const data = {
        id,
        name: values.name,
        startDate: startDateTime,
        endDate: endDateTime,
        status: values.status,
        cashbackType: values.generation,
        cashbackValue: toNumber(values.cashback),
        calculationBasis: values.calculation_basis,
        daysToExpire: toNumber(values.days_to_expire),
        physicalStoreDays: toNumber(values.physical_store_days),
        ecommerceDays: toNumber(values.ecommerce_days),
        minimumAccumulation: toNumber(values.minimum_accumulation),
        maximumAccumulation: toNumber(values.maximum_accumulation),
        minimumOrder: toNumber(values.minimum_order),
        rescueValue: toNumber(values.rescue_value),
        maxRedemptionValue: toNumber(values.maximum_rescue),
        minimumPurchaseRedemption: toNumber(values.minimum_purchase_redemption),
        blockDiscounts: values.block_discounts,
        blockVtexAnyCoupon: values.block_vtex_any_coupon,
        redemptionBlockDiscounts: values.redemption_block_discounts,
        redemptionBlockVtexAnyCoupon: values.redemption_block_vtex_any_coupon,
        redemptionBlockVtexPriceRemarked:
          values.redemption_block_vtex_price_remarked,
      };

      try {
        await campaignService.updateCampaign(id, data);

        console.log('Campaign updated:', data);
        toast.success(props.t('campaigns.edit-success'));
        setIsSaving(false);
        navigate('/estrategias/campanhas');
      } catch (error) {
        console.error('Error updating campaign:', error);
        setIsSaving(false);
        const errorMessage = getCampaignErrorMessage(error, props.t);
        toast.error(errorMessage);
      }
    },
  });

  const isInvalid = (field) => {
    return formik.touched[field] && formik.errors[field];
  };

  const generationOptions = [
    { value: 'percentage', label: props.t('campaigns.form.percentage') },
    { value: 'fixed', label: props.t('campaigns.form.cashback-fixed') },
  ];

  const calculationBasisOptions = [
    { value: 'total', label: props.t('campaigns.form.calculation-total') },
    { value: 'paid', label: props.t('campaigns.form.calculation-paid') },
  ];

  const statusOptions = [
    { value: 'scheduled', label: props.t('campaigns.status-scheduled') },
    { value: 'active', label: props.t('campaigns.status-active') },
    { value: 'paused', label: props.t('campaigns.status-paused') },
  ];

  const getStatusBadge = (status) => {
    const classes = {
      scheduled: 'bg-info-subtle text-info',
      active: 'bg-success-subtle text-success',
      paused: 'bg-warning-subtle text-warning',
      completed: 'bg-secondary-subtle text-secondary',
      canceled: 'bg-danger-subtle text-danger',
    };
    const label =
      statusOptions.find((opt) => opt.value === status)?.label || status;
    return <Badge className={classes[status] || 'bg-secondary'}>{label}</Badge>;
  };

  const breadcrumbItems = [
    { label: props.t('strategy-title-menu'), link: '/estrategias/conta' },
    { label: props.t('campaigns.breadcrumb'), link: '/estrategias/campanhas' },
    { label: props.t('campaigns.edit'), active: true },
  ];

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={props.t('campaigns.edit')}
          breadcrumbItems={breadcrumbItems}
        />

        <Row>
          <Col
            lg={12}
            className="d-flex justify-content-between align-items-center mb-4"
          >
            <h4 className="card-title mb-0">{props.t('campaigns.edit')}</h4>
            <div className="d-flex gap-2">
              <Button
                color="secondary"
                onClick={() => navigate('/estrategias/campanhas')}
              >
                {props.t('cancel')}
              </Button>
            </div>
          </Col>
        </Row>

        <Form
          className="tablelist-form"
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
            return false;
          }}
        >
          <Row className="mb-4">
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    {props.t('campaigns.wizard.step1-title')}
                  </h4>
                </CardHeader>
                <CardBody>
                  <Row className="flex-column gap-3 flex-lg-row">
                    <Col lg={6}>
                      <Label className="form-label" htmlFor="name">
                        {props.t('campaigns.form.name')} *
                      </Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        placeholder={props.t('name-placeholder')}
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={isInvalid('name')}
                      />
                      {isInvalid('name') && (
                        <FormFeedback>{formik.errors.name}</FormFeedback>
                      )}
                    </Col>

                    <Col lg={6}>
                      <Label className="form-label" htmlFor="status">
                        {props.t('campaigns.table.status')}
                      </Label>
                      <div className="mt-1">
                        {getStatusBadge(formik.values.status)}
                      </div>
                    </Col>

                    <Col lg={6}>
                      <Label className="form-label" htmlFor="startDate">
                        {props.t('campaigns.form.start-date')} *
                      </Label>
                      <div className="flatpickr">
                        <Flatpickr
                          id="start-date"
                          className="form-control"
                          options={{
                            locale: Portuguese,
                            dateFormat: 'd/m/Y',
                            allowInput: true,
                          }}
                          value={parseDateString(formik.values.startDate)}
                          onChange={(dates) => {
                            if (dates[0]) {
                              formik.setFieldValue(
                                'startDate',
                                dates[0].toISOString().split('T')[0]
                              );
                            } else {
                              formik.setFieldValue('startDate', '');
                            }
                          }}
                          placeholder={props.t(
                            'campaigns.form.date-placeholder'
                          )}
                        />
                      </div>
                      {isInvalid('startDate') && (
                        <div className="text-danger small mt-1">
                          {formik.errors.startDate}
                        </div>
                      )}
                    </Col>

                    <Col lg={6}>
                      <Label className="form-label" htmlFor="start-time">
                        {props.t('campaigns.form.start-time')}
                      </Label>
                      <div className="flatpickr">
                        <Flatpickr
                          id="start-time"
                          className="form-control"
                          options={{
                            locale: Portuguese,
                            enableTime: true,
                            noCalendar: true,
                            dateFormat: 'H:i',
                            time_24hr: true,
                          }}
                          value={parseTimeString(formik.values.startTime)}
                          onChange={(dates) => {
                            if (dates[0]) {
                              formik.setFieldValue(
                                'startTime',
                                dates[0].toTimeString().slice(0, 5)
                              );
                            } else {
                              formik.setFieldValue('startTime', '');
                            }
                          }}
                          placeholder={props.t(
                            'campaigns.form.time-placeholder'
                          )}
                        />
                      </div>
                    </Col>

                    <Col lg={6}>
                      <Label className="form-label" htmlFor="endDate">
                        {props.t('campaigns.form.end-date')} *
                      </Label>
                      <div className="flatpickr">
                        <Flatpickr
                          id="end-date"
                          className="form-control"
                          options={{
                            locale: Portuguese,
                            dateFormat: 'd/m/Y',
                            allowInput: true,
                          }}
                          value={parseDateString(formik.values.endDate)}
                          onChange={(dates) => {
                            if (dates[0]) {
                              formik.setFieldValue(
                                'endDate',
                                dates[0].toISOString().split('T')[0]
                              );
                            } else {
                              formik.setFieldValue('endDate', '');
                            }
                          }}
                          placeholder={props.t(
                            'campaigns.form.date-placeholder'
                          )}
                        />
                      </div>
                      {isInvalid('endDate') && (
                        <div className="text-danger small mt-1">
                          {formik.errors.endDate}
                        </div>
                      )}
                    </Col>

                    <Col lg={6}>
                      <Label className="form-label" htmlFor="end-time">
                        {props.t('campaigns.form.end-time')}
                      </Label>
                      <div className="flatpickr">
                        <Flatpickr
                          id="end-time"
                          className="form-control"
                          options={{
                            locale: Portuguese,
                            enableTime: true,
                            noCalendar: true,
                            dateFormat: 'H:i',
                            time_24hr: true,
                          }}
                          value={parseTimeString(formik.values.endTime)}
                          onChange={(dates) => {
                            if (dates[0]) {
                              formik.setFieldValue(
                                'endTime',
                                dates[0].toTimeString().slice(0, 5)
                              );
                            } else {
                              formik.setFieldValue('endTime', '');
                            }
                          }}
                          placeholder={props.t(
                            'campaigns.form.time-placeholder'
                          )}
                        />
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    {props.t('campaigns.wizard.step2')}
                  </h4>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-column gap-3">
                    <div>
                      <h6 style={{ fontWeight: 'bold' }}>
                        {props.t(
                          'campaigns.form.configure-cashback-generation'
                        )}
                      </h6>
                      <Row className="flex-column gap-3 flex-lg-row mt-3">
                        <Col lg={4}>
                          <Label className="form-label" htmlFor="generation">
                            {props.t('campaigns.form.cashback-type')} *
                          </Label>
                          <Select
                            id="generation"
                            name="generation"
                            value={generationOptions.find(
                              (opt) => opt.value === formik.values.generation
                            )}
                            onChange={(option) =>
                              formik.setFieldValue('generation', option.value)
                            }
                            options={generationOptions}
                            placeholder="Selecione"
                          />
                          {isInvalid('generation') && (
                            <div className="text-danger small mt-1">
                              {formik.errors.generation}
                            </div>
                          )}
                        </Col>

                        <Col lg={4}>
                          <Label className="form-label" htmlFor="cashback">
                            {props.t('campaigns.form.cashback-value')} *
                          </Label>
                          {formik.values.generation === 'percentage' && (
                            <div className="input-group">
                              <Input
                                type="text"
                                id="cashback"
                                name="cashback"
                                className="form-control"
                                placeholder={props.t(
                                  'campaigns.form.define-percentage'
                                )}
                                value={formik.values.cashback}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={isInvalid('cashback')}
                              />
                              <span className="input-group-text">%</span>
                              {isInvalid('cashback') && (
                                <FormFeedback>
                                  {formik.errors.cashback}
                                </FormFeedback>
                              )}
                            </div>
                          )}
                          {formik.values.generation === 'fixed' && (
                            <div className="input-group">
                              <span className="input-group-text">
                                {props.t('currency')}
                              </span>
                              <CurrencyInput
                                placeholder="0,00"
                                value={formik.values.cashback}
                                onChange={(e) =>
                                  formik.setFieldValue('cashback', e)
                                }
                                invalid={isInvalid('cashback')}
                              />
                              {isInvalid('cashback') && (
                                <FormFeedback>
                                  {formik.errors.cashback}
                                </FormFeedback>
                              )}
                            </div>
                          )}
                        </Col>

                        <Col lg={4}>
                          <Label
                            className="form-label"
                            htmlFor="calculation_basis"
                          >
                            {props.t('campaigns.form.calculation-basis')} *
                          </Label>
                          <Select
                            id="calculation_basis"
                            name="calculation_basis"
                            value={calculationBasisOptions.find(
                              (opt) =>
                                opt.value === formik.values.calculation_basis
                            )}
                            onChange={(option) =>
                              formik.setFieldValue(
                                'calculation_basis',
                                option.value
                              )
                            }
                            options={calculationBasisOptions}
                            placeholder="Selecione"
                          />
                          {isInvalid('calculation_basis') && (
                            <div className="text-danger small mt-1">
                              {formik.errors.calculation_basis}
                            </div>
                          )}
                        </Col>
                      </Row>
                    </div>

                    <Row>
                      <Col xs={6}>
                        <Label
                          className="form-label"
                          htmlFor="minimum_accumulation"
                        >
                          {props.t('campaigns.form.minimum-cashback-generated')}
                        </Label>
                        <div className="input-group">
                          <span className="input-group-text">
                            {props.t('currency')}
                          </span>
                          <CurrencyInput
                            placeholder="0,00"
                            value={formik.values.minimum_accumulation}
                            onChange={(e) =>
                              formik.setFieldValue('minimum_accumulation', e)
                            }
                          />
                        </div>
                      </Col>
                      <Col xs={6}>
                        <Label
                          className="form-label"
                          htmlFor="maximum_accumulation"
                        >
                          {props.t('campaigns.form.maximum-cashback-generated')}
                        </Label>
                        <div className="input-group">
                          <span className="input-group-text">
                            {props.t('currency')}
                          </span>
                          <CurrencyInput
                            placeholder="0,00"
                            value={formik.values.maximum_accumulation}
                            onChange={(e) =>
                              formik.setFieldValue('maximum_accumulation', e)
                            }
                          />
                        </div>
                      </Col>
                    </Row>

                    <div
                      className="d-flex flex-column gap-3 mb-2 pb-4"
                      style={{ borderBottom: '2px solid #eee' }}
                    >
                      <h6 style={{ fontWeight: 'bold' }}>
                        {props.t('campaigns.form.expiration-period')}
                      </h6>
                      <Row className="flex-column gap-3 flex-lg-row">
                        <Col lg={12}>
                          <Label
                            className="form-label"
                            htmlFor="days_to_expire"
                          >
                            {props.t('campaigns.form.expiration-days')}
                          </Label>
                          <div className="input-group">
                            <Input
                              type="number"
                              id="days_to_expire"
                              name="days_to_expire"
                              className="form-control"
                              min={1}
                              value={formik.values.days_to_expire}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={isInvalid('days_to_expire')}
                            />
                            <span className="input-group-text">
                              {props.t('campaigns.form.days')}
                            </span>
                            {isInvalid('days_to_expire') && (
                              <FormFeedback>
                                {formik.errors.days_to_expire}
                              </FormFeedback>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div
                      className="d-flex flex-column gap-3 mb-2 pb-4"
                      style={{ borderBottom: '2px solid #eee' }}
                    >
                      <h6 style={{ fontWeight: 'bold' }}>
                        {props.t('campaigns.form.availability-period')}
                      </h6>
                      <Row className="flex-column gap-3 flex-lg-row">
                        <Col lg={6}>
                          <Label
                            className="form-label"
                            htmlFor="physical_store_days"
                          >
                            {props.t(
                              'campaigns.form.physical-store-availability'
                            )}
                          </Label>
                          <div className="input-group">
                            <Input
                              type="number"
                              id="physical_store_days"
                              name="physical_store_days"
                              className="form-control"
                              min={0}
                              value={formik.values.physical_store_days}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            <span className="input-group-text">
                              {props.t('campaigns.form.days')}
                            </span>
                          </div>
                        </Col>
                        <Col lg={6}>
                          <Label
                            className="form-label"
                            htmlFor="ecommerce_days"
                          >
                            {props.t('campaigns.form.ecommerce-availability')}
                          </Label>
                          <div className="input-group">
                            <Input
                              type="number"
                              id="ecommerce_days"
                              name="ecommerce_days"
                              className="form-control"
                              min={0}
                              value={formik.values.ecommerce_days}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            <span className="input-group-text">
                              {props.t('campaigns.form.days')}
                            </span>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div className="mt-3">
                      <h6 style={{ fontWeight: 'bold' }}>
                        {props.t('campaigns.form.other-settings')}
                      </h6>
                      <Row className="mt-3">
                        <Col xs={12}>
                          <div className="form-check form-switch d-flex mt-2 gap-1 align-items-center">
                            <Input
                              className="form-check-input mt-0"
                              type="checkbox"
                              id="block_discounts"
                              name="block_discounts"
                              checked={formik.values.block_discounts}
                              onChange={formik.handleChange}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="block_discounts"
                            >
                              {props.t(
                                'campaigns.form.block-accumulation-discount'
                              )}
                            </Label>
                          </div>
                        </Col>
                        <Col xs={12}>
                          <div className="form-check form-switch d-flex mt-2 gap-1 align-items-center">
                            <Input
                              className="form-check-input mt-0"
                              type="checkbox"
                              id="block_vtex_any_coupon"
                              name="block_vtex_any_coupon"
                              checked={formik.values.block_vtex_any_coupon}
                              onChange={formik.handleChange}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="block_vtex_any_coupon"
                            >
                              {props.t(
                                'campaigns.form.block-accumulation-coupon'
                              )}
                            </Label>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    {props.t('campaigns.wizard.step3')}
                  </h4>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-column gap-3">
                    <div>
                      <h6 style={{ fontWeight: 'bold' }}>
                        {props.t(
                          'campaigns.form.configure-cashback-redemption'
                        )}
                      </h6>
                      <Row className="flex-column gap-3 flex-lg-row mt-3">
                        <Col lg={4}>
                          <Label className="form-label" htmlFor="rescue_value">
                            {props.t('campaigns.form.maximum-discount')} (%
                            cashback)
                          </Label>
                          <div className="input-group">
                            <Input
                              type="text"
                              id="rescue_value"
                              name="rescue_value"
                              className="form-control"
                              placeholder="0,00"
                              value={formik.values.rescue_value}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            <span className="input-group-text">%</span>
                          </div>
                        </Col>

                        <Col lg={4}>
                          <Label className="form-label" htmlFor="minimum_order">
                            {props.t(
                              'campaigns.form.minimum-purchase-redemption'
                            )}
                          </Label>
                          <div className="input-group">
                            <span className="input-group-text">
                              {props.t('currency')}
                            </span>
                            <CurrencyInput
                              placeholder="0,00"
                              value={formik.values.minimum_order}
                              onChange={(e) =>
                                formik.setFieldValue('minimum_order', e)
                              }
                            />
                          </div>
                        </Col>

                        <Col lg={4}>
                          <Label
                            className="form-label"
                            htmlFor="maximum_rescue"
                          >
                            {props.t('campaigns.form.maximum-redemption-value')}
                          </Label>
                          <div className="input-group">
                            <span className="input-group-text">
                              {props.t('currency')}
                            </span>
                            <CurrencyInput
                              placeholder="0,00"
                              value={formik.values.maximum_rescue}
                              onChange={(e) =>
                                formik.setFieldValue('maximum_rescue', e)
                              }
                              invalid={isInvalid('maximum_rescue')}
                            />
                            {isInvalid('maximum_rescue') && (
                              <FormFeedback>
                                {formik.errors.maximum_rescue}
                              </FormFeedback>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div className="d-flex flex-column gap-3 mt-3">
                      <h6 style={{ fontWeight: 'bold' }}>
                        {props.t('campaigns.form.block-settings')}
                      </h6>
                      <Row className="mt-3">
                        <Col xs={12}>
                          <div className="form-check form-switch d-flex mt-2 gap-1 align-items-center">
                            <Input
                              className="form-check-input mt-0"
                              type="checkbox"
                              id="redemption_block_discounts"
                              name="redemption_block_discounts"
                              checked={formik.values.redemption_block_discounts}
                              onChange={formik.handleChange}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="redemption_block_discounts"
                            >
                              {props.t(
                                'campaigns.form.block-redemption-discount'
                              )}
                            </Label>
                          </div>
                        </Col>
                        <Col xs={12}>
                          <div className="form-check form-switch d-flex mt-2 gap-1 align-items-center">
                            <Input
                              className="form-check-input mt-0"
                              type="checkbox"
                              id="redemption_block_vtex_any_coupon"
                              name="redemption_block_vtex_any_coupon"
                              checked={
                                formik.values.redemption_block_vtex_any_coupon
                              }
                              onChange={formik.handleChange}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="redemption_block_vtex_any_coupon"
                            >
                              {props.t(
                                'campaigns.form.block-redemption-coupon'
                              )}
                            </Label>
                          </div>
                        </Col>
                        <Col xs={12}>
                          <div className="form-check form-switch d-flex mt-2 gap-1 align-items-center">
                            <Input
                              className="form-check-input mt-0"
                              type="checkbox"
                              id="redemption_block_vtex_price_remarked"
                              name="redemption_block_vtex_price_remarked"
                              checked={
                                formik.values
                                  .redemption_block_vtex_price_remarked
                              }
                              onChange={formik.handleChange}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="redemption_block_vtex_price_remarked"
                            >
                              {props.t(
                                'campaigns.form.block-redemption-remarked'
                              )}
                            </Label>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <div className="d-flex mt-4">
                <button
                  type="submit"
                  disabled={!formik.isValid || formik.isSubmitting || isSaving}
                  className="btn btn-success btn-label right ms-auto"
                >
                  <i className="ri-check-line label-icon align-middle fs-16 ms-2"></i>
                  {props.t('save')}
                  {isSaving && <Spinner className="ms-2" size="sm" />}
                </button>
              </div>
            </Col>
          </Row>
        </Form>

        <ToastContainer />
      </Container>
    </div>
  );
};

export default withTranslation()(EditCampaign);
